/**
 * /equipment/pressure-control/calibrations — test gauges and their
 * calibrations (api#1281, BOP-2). One row per gauge: its latest calibration and
 * the API's state for it. A row opens the gauge on its own page, with its
 * history.
 *
 * A pressure test refuses a gauge whose calibration is not valid on the test
 * day, so this list is where the office keeps that from happening.
 *
 * `due_on` IS THE LAST VALID DAY (the API's word), not the first invalid one:
 * a calibration due 2027-03-01 is still good on 2027-03-01. The dialog says so.
 *
 * Only an admin or office staff (or an elevated dispatcher) records one; the
 * API is PC_ADMIN on POST /calibrations. Append-only — a correction is a new
 * calibration, which becomes the current one if it is the latest.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { PcBadge, PcErrorLine, PcSectionNav, usePcEquipmentOptions } from '../../components/pressure-control/PcSectionNav';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { usePcCalibrations, usePcComponents, useRecordPcCalibration } from '../../hooks/usePressureControl';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { todayCalendarDate } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canAdminPc,
  PC_CAL_FILL,
  PC_CAL_LABEL,
  PC_CAL_RAIL,
  PC_CALIBRATIONS,
  pcDay,
  pcDueDaysPhrase,
  pcErrorMessage,
  pcPressure,
} from '../../lib/pressure-control';
import type { PcCalibration } from '../../types/pressure-control';
import { DH_DATE_INPUT, DH_INPUT, DH_SECONDARY, DhDialog, DhEmpty, DhField, DhPanel, useOpenCount } from '../downhole/DownholeBits';
import { useOpenRecord } from '../downhole/recordPage';

/** The latest calibration per gauge (by calibrated day, then recorded time). */
export function latestPerGauge(rows: PcCalibration[]): PcCalibration[] {
  const best = new Map<string, PcCalibration>();
  for (const r of rows) {
    const cur = best.get(r.equipmentId);
    if (!cur || r.calibratedOn > cur.calibratedOn || (r.calibratedOn === cur.calibratedOn && (r.createdAt ?? '') > (cur.createdAt ?? ''))) {
      best.set(r.equipmentId, r);
    }
  }
  return Array.from(best.values());
}

/** Worst first: overdue, due soon, valid; then the soonest due. */
const RANK: Record<string, number> = { overdue: 0, never: 1, due_soon: 2, valid: 3 };
export const sortGauges = (rows: PcCalibration[]) =>
  [...rows].sort((a, b) => (RANK[a.state.status] ?? 9) - (RANK[b.state.status] ?? 9) || a.dueOn.localeCompare(b.dueOn));

/** Parse an optional decimal field. `undefined` = blank; NaN = junk. */
function decimal(v: string): number | undefined {
  const t = v.trim().replace(/,/g, '');
  if (!t) return undefined;
  return /^\d+(\.\d+)?$/.test(t) ? Number(t) : NaN;
}

export function RecordCalibrationDialog({
  open, onClose, equipmentId, unit,
}: { open: boolean; onClose: () => void; equipmentId?: string; unit: string | null }) {
  const tz = useTenantTimeZone();
  const record = useRecordPcCalibration();
  const openCount = useOpenCount(open);
  const equipment = usePcEquipmentOptions(open && !equipmentId);
  const today = todayCalendarDate(tz) ?? undefined;
  const [gauge, setGauge] = useState('');
  const [calibratedOn, setCalibratedOn] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [rangeMin, setRangeMin] = useState('');
  const [rangeMax, setRangeMax] = useState('');
  const [accuracy, setAccuracy] = useState('');
  const [certificate, setCertificate] = useState('');
  const [performedBy, setPerformedBy] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setGauge(equipmentId ?? ''); setCalibratedOn(''); setDueOn(''); setRangeMin(''); setRangeMax('');
    setAccuracy(''); setCertificate(''); setPerformedBy(''); setNotes(''); setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openCount]);

  const u = unit ? ` (${unit})` : '';
  const submit = async () => {
    setError(null);
    if (!gauge) return setError('Choose the gauge.');
    if (!calibratedOn) return setError('Enter the day it was calibrated.');
    if (today && calibratedOn > today) return setError('The calibration day cannot be after today.');
    if (!dueOn) return setError('Enter the last day the calibration is valid.');
    if (dueOn < calibratedOn) return setError('The last valid day cannot be before the calibration day.');
    const max = decimal(rangeMax);
    if (max === undefined || Number.isNaN(max) || max <= 0) return setError(`Enter the gauge's calibrated maximum${u}, a number greater than 0.`);
    const min = decimal(rangeMin);
    if (min !== undefined && Number.isNaN(min)) return setError(`The calibrated minimum must be a number${u}.`);
    if (min !== undefined && min >= max) return setError('The calibrated minimum must be below the maximum.');
    try {
      await record.mutateAsync({
        equipment_id: gauge,
        calibrated_on: calibratedOn,
        due_on: dueOn,
        range_min: min ?? null,
        range_max: max,
        accuracy_class: accuracy.trim() || null,
        certificate_number: certificate.trim() || null,
        performed_by: performedBy.trim() || null,
        notes: notes.trim() || null,
      });
      toast.success('Calibration recorded');
      onClose();
    } catch (e) {
      setError(pcErrorMessage(e, 'The calibration could not be recorded.'));
    }
  };

  return (
    <DhDialog
      open={open}
      onClose={onClose}
      title="Record a gauge calibration"
      wide
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>Cancel</button>
          <button type="button" onClick={submit} disabled={record.isPending} className="fi-action fi-action--standard fi-action--primary">
            {record.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Record calibration
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {!equipmentId && (
          <div className="sm:col-span-2">
            <DhField label="Gauge" hint="Any equipment row: the gauge or chart recorder used on tests.">
              <select className={DH_INPUT} value={gauge} onChange={(e) => setGauge(e.target.value)} aria-label="Gauge">
                <option value="">Choose…</option>
                {(equipment.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </DhField>
          </div>
        )}
        <DhField label="Calibrated on">
          <input type="date" className={DH_DATE_INPUT} value={calibratedOn} max={today} onChange={(e) => setCalibratedOn(e.target.value)} aria-label="Calibrated on" />
        </DhField>
        <DhField label="Valid through" hint="The last day the calibration is good for, from its certificate.">
          <input type="date" className={DH_DATE_INPUT} value={dueOn} min={calibratedOn || undefined} onChange={(e) => setDueOn(e.target.value)} aria-label="Valid through" />
        </DhField>
        <DhField label={`Calibrated minimum${u}`} hint="Optional.">
          <input className={DH_INPUT} inputMode="decimal" value={rangeMin} onChange={(e) => setRangeMin(e.target.value)} aria-label="Calibrated minimum" />
        </DhField>
        <DhField label={`Calibrated maximum${u}`} hint="A test pressure above this is refused.">
          <input className={DH_INPUT} inputMode="decimal" value={rangeMax} onChange={(e) => setRangeMax(e.target.value)} aria-label="Calibrated maximum" />
        </DhField>
        <DhField label="Accuracy class">
          <input className={DH_INPUT} value={accuracy} maxLength={50} onChange={(e) => setAccuracy(e.target.value)} aria-label="Accuracy class" />
        </DhField>
        <DhField label="Certificate number">
          <input className={DH_INPUT} value={certificate} maxLength={100} onChange={(e) => setCertificate(e.target.value)} aria-label="Certificate number" />
        </DhField>
        <div className="sm:col-span-2">
          <DhField label="Calibrated by" hint="The lab or person.">
            <input className={DH_INPUT} value={performedBy} maxLength={200} onChange={(e) => setPerformedBy(e.target.value)} aria-label="Calibrated by" />
          </DhField>
        </div>
        <div className="sm:col-span-2">
          <DhField label="Notes">
            <textarea className={`${DH_INPUT} h-20 py-2`} value={notes} maxLength={4000} onChange={(e) => setNotes(e.target.value)} aria-label="Notes" />
          </DhField>
        </div>
      </div>
      <PcErrorLine message={error} />
    </DhDialog>
  );
}

export default function PressureControlCalibrationsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const elevated = useCanWriteCompanySettings();
  const canRecord = canAdminPc(user?.role, elevated);
  const [recording, setRecording] = useState(false);
  const query = usePcCalibrations();
  const names = usePcEquipmentOptions(true);
  const meta = usePcComponents();
  const unit = meta.data?.meta?.pressureUnit ?? null;
  const failure = queryLoadFailure(query);
  const rows = useMemo(() => sortGauges(latestPerGauge(query.data ?? [])), [query.data]);
  const nameOf = (id: string) => names.data?.find((n) => n.id === id)?.name ?? '—';

  const open = (id: string) => openRecord(`${PC_CALIBRATIONS}/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };

  return (
    <div data-testid="pc-calibrations-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pressure control"
        title="Test gauges"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canRecord ? (
            <button type="button" onClick={() => setRecording(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Record calibration
            </button>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PcSectionNav />
          <DhPanel
            eyebrow="A test refuses a gauge out of calibration"
            title="Gauges"
            testId="pc-gauges-register"
            footer={
              !query.isLoading && !failure && rows.length > 0
                ? `${rows.length} ${rows.length === 1 ? 'gauge' : 'gauges'} · latest calibration each${canRecord ? '' : ' · only an admin or office staff can record a calibration'}`
                : undefined
            }
          >
            {query.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading calibrations" />
              </div>
            ) : failure ? (
              <LoadFailureNotice
                error={queryFailureError(query)}
                kind={failure}
                subject="gauge calibrations"
                allowed="admins, office staff, supervisors, dispatchers, field workers and logistics"
              />
            ) : rows.length === 0 ? (
              <DhEmpty>No gauge has a calibration on record.{canRecord && ' Record one with Record calibration.'}</DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[900px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Gauge</th>
                        <th scope="col" className="px-4 py-3">Calibrated</th>
                        <th scope="col" className="px-4 py-3">Valid through</th>
                        <th scope="col" className="px-4 py-3">Range</th>
                        <th scope="col" className="px-4 py-3">Certificate</th>
                        <th scope="col" className="px-4 py-3">State</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr
                          key={r.equipmentId}
                          data-testid="pc-gauge-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => open(r.equipmentId)}
                          onKeyDown={rowKeys(r.equipmentId)}
                          className={`cursor-pointer border-l-4 border-t border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${PC_CAL_RAIL[r.state.status] ?? 'border-l-transparent'}`}
                        >
                          <td className="px-4 py-3 text-sm font-semibold text-white">{nameOf(r.equipmentId)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{pcDay(r.calibratedOn)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">
                            {pcDay(r.dueOn)}
                            <span className="block text-xs text-fieldiq-muted">{pcDueDaysPhrase(r.state.daysUntilDue)}</span>
                          </td>
                          <td className="px-4 py-3 font-mono text-sm tabular-nums text-fieldiq-text">
                            {r.rangeMin !== null ? `${pcPressure(r.rangeMin, null)} – ` : ''}{pcPressure(r.rangeMax, r.pressureUnit)}
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{r.certificateNumber ?? '—'}</td>
                          <td className="px-4 py-3"><PcBadge value={r.state.status} labels={PC_CAL_LABEL} fills={PC_CAL_FILL} testId="pc-cal-badge" /></td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {rows.map((r) => (
                    <div
                      key={r.equipmentId}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(r.equipmentId)}
                      onKeyDown={rowKeys(r.equipmentId)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <p className="flex flex-wrap items-center gap-2 text-sm font-extrabold text-white">
                        {nameOf(r.equipmentId)}
                        <PcBadge value={r.state.status} labels={PC_CAL_LABEL} fills={PC_CAL_FILL} />
                      </p>
                      <p className="mt-1 text-xs text-fieldiq-muted">Valid through {pcDay(r.dueOn)} · {pcDueDaysPhrase(r.state.daysUntilDue)}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>
        </div>
      </section>
      {canRecord && <RecordCalibrationDialog open={recording} onClose={() => setRecording(false)} unit={unit} />}
    </div>
  );
}
